import React, { useEffect, useState, useMemo } from 'react';
import { Modal, Button, Checkbox, Spin } from 'antd';
import { OrgCredentialingPayerRef, TKlarityPayer } from 'types/credentialing';
import usePayerOptions from 'hooks/credentialing/usePayerOptions';
import { UhcGroupNotice } from 'components/CredentialingPayerSteps';
import s from './s.module.less';

interface PayerPickerModalProps {
    open: boolean;
    states: string[];
    value: OrgCredentialingPayerRef[];
    onCancel: () => void;
    onSave: (payers: OrgCredentialingPayerRef[]) => void;
    stateCodeMap: Record<string, string>;
    // 本次提交 roster 的 provider 数，驱动 UHC <20 人的蓝色通知
    rosterSize?: number;
}

const PayerPickerModal: React.FC<PayerPickerModalProps> = ({
    open,
    states,
    value,
    onCancel,
    onSave,
    stateCodeMap,
    rosterSize = 0,
}) => {
    const multi = states.length > 1;
    const [selected, setSelected] = useState<OrgCredentialingPayerRef[]>(value);
    const [activeState, setActiveState] = useState<string>(states[0] || '');
    const [payers, loading] = usePayerOptions(states, states.length > 0);

    // Reset state and active state when modal opens
    useEffect(() => {
        if (open) {
            setSelected(value);
            setActiveState(states[0] || '');
        }
    }, [open, value, states]);

    // Filter payers for active state
    const statePayers = useMemo(() => {
        return payers.filter((p) => p.state === activeState).filter((p) => p.status === 'ACTIVE');
    }, [payers, activeState]);

    const excludedPayerIds = useMemo(() => {
        const excluded = new Set<string>();
        for (const sel of selected) {
            const payer = payers.find((p) => p.id === sel.klarityPayerId && p.state === sel.state);
            if (payer?.exclusivePayerIds) {
                payer.exclusivePayerIds.split(',').forEach((id) => excluded.add(id.trim()));
            }
        }
        return excluded;
    }, [selected, payers]);

    const toggle = (payer: TKlarityPayer) => {
        const isCurrentlySelected = selected.some((p) => p.klarityPayerId === payer.id && p.state === payer.state);
        if (!isCurrentlySelected && excludedPayerIds.has(String(payer.id))) return;
        setSelected((prev) => {
            const exists = prev.find((p) => p.klarityPayerId === payer.id && p.state === payer.state);
            if (exists) {
                return prev.filter((p) => !(p.klarityPayerId === payer.id && p.state === payer.state));
            }
            return [
                ...prev,
                {
                    klarityPayerId: payer.id,
                    payerName: payer.name,
                    state: payer.state,
                    highDemand: payer.highDemand,
                },
            ];
        });
    };

    const isSelected = (payer: TKlarityPayer) =>
        selected.some((p) => p.klarityPayerId === payer.id && p.state === payer.state);

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            footer={null}
            width={880}
            className={s.modal}
            title={null}
            destroyOnClose
        >
            <Spin spinning={loading}>
                <div className={s.header}>
                    <div className={s.title}>Payers your practice will credential for</div>
                    <div className={s.subtitle}>
                        Pick the payers the practice is enrolling with. Every provider inherits these by default; you
                        can adjust per provider later.
                    </div>
                </div>

                <div className={s.body}>
                    <UhcGroupNotice selectedPayerNames={selected.map((p) => p.payerName)} rosterSize={rosterSize} />
                    {multi && (
                        <div className={s.stateChips}>
                            {states.map((st) => (
                                <div
                                    key={st}
                                    className={`${s.stateChip} ${st === activeState ? s.stateChipActive : ''}`}
                                    onClick={() => setActiveState(st)}
                                >
                                    {stateCodeMap[st] || st}
                                </div>
                            ))}
                        </div>
                    )}

                    {multi && (
                        <div className={s.sectionLabel}>
                            Available now in {stateCodeMap[activeState] || activeState}
                        </div>
                    )}

                    <div className={s.payerList}>
                        {statePayers.map((p) => {
                            const checked = isSelected(p);
                            const isExcluded = excludedPayerIds.has(String(p.id)) && !checked;
                            return (
                                <div
                                    key={`${p.id}-${p.state}`}
                                    className={`${s.payer} ${checked ? s.payerSelected : ''} ${isExcluded ? s.payerDisabled : ''}`}
                                    onClick={() => toggle(p)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            toggle(p);
                                        }
                                    }}
                                >
                                    <Checkbox
                                        checked={checked}
                                        disabled={isExcluded}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={() => toggle(p)}
                                        className={s.checkboxWrapper}
                                    />
                                    <span className={s.payerInfo}>
                                        <span className={s.payerName}>
                                            {p.name}
                                            {isExcluded && p.payerType && <span className={s.alreadyTag}>Only one {p.payerType} panel is allowed</span>}
                                            {p.highDemand === 1 && <span className={s.demandTag}>High demand</span>}
                                        </span>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className={s.footer}>
                    <Button type="primary" className={s.doneBtn} onClick={() => onSave(selected)}>
                        Done
                    </Button>
                </div>
            </Spin>
        </Modal>
    );
};

export default PayerPickerModal;
